// Import Dependencies
import { CalendarIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { forwardRef, useEffect, useRef, useMemo } from "react";
import { BaseOptions } from "flatpickr/dist/types/options";
import flatpickrCSS from "flatpickr/dist/themes/light.css?inline";

// Local Imports
import { Input, InputProps } from "../../ui/Form/Input";
import { useMergedRef } from "../../../hooks/useMergedRef";
import {
  injectStyles,
  insertStylesToHead,
  makeStyleTag,
} from "../../../utils/dom/injectStylesToHead";
import "./Datepicker.css";

import { Flatpickr } from "./Flatpickr/Flatpickr";
import type { FlatpickrProps, FlatpickrRef } from "./Flatpickr/types";

// ----------------------------------------------------------------------

// Define prop types for the DatePicker component
interface DatePickerProps
  extends Omit<FlatpickrProps, "options">,
    Omit<
      InputProps<"input">,
      "defaultValue" | "value" | "onChange" | "prefix" | "type"
    > {
  options?: Partial<BaseOptions>;
  isCalendar?: boolean;
  hasCalenderIcon?: boolean;
}

const styles = `@layer vendor {
  ${flatpickrCSS}
}`;

const sheet = makeStyleTag();

injectStyles(sheet, styles);
insertStylesToHead(sheet);

const DatePicker = forwardRef<FlatpickrRef, DatePickerProps>(
  (
    {
      options: userOptions,
      className,
      isCalendar = false,
      hasCalenderIcon = true,
      ...props
    },
    ref,
  ) => {
    const flatpickrRef = useRef<FlatpickrRef | null>(null);

    const options = {
      inline: isCalendar,
      ...userOptions,
    };

    const mergedRef = useMergedRef(flatpickrRef, ref);

    useEffect(() => {
      const calendarContainer =
        flatpickrRef.current?._flatpickr?.calendarContainer;
      if (calendarContainer) {
        calendarContainer.classList.toggle("is-calendar", isCalendar);
      }
    }, [isCalendar]);

    const renderComponent = useMemo(
      () => (props: any, ref: any) => {
        if (isCalendar) {
          return (
            <div className="flex flex-col">
              {props.label && (
                <label className="input-label text-sm font-medium text-gray-700 dark:text-dark-200 mb-1.5">
                  {props.label}
                  {props.required && (
                    <span className="required-indicator" aria-label="required">*</span>
                  )}
                </label>
              )}
              <input ref={ref} readOnly {...props} />
              {props.error && (
                <div className="input-text-error mt-1 text-xs text-error dark:text-error-lighter">
                  {typeof props.error === "string" ? props.error : "This field is required"}
                </div>
              )}
            </div>
          );
        }
        return (
          <Input
            ref={ref}
            prefix={
              !userOptions?.inline &&
              hasCalenderIcon && <CalendarIcon className="size-5" />
            }
            readOnly
            {...props}
          />
        );
      },
      [isCalendar, hasCalenderIcon, userOptions?.inline],
    );

    return (
      <Flatpickr
        className={clsx("cursor-pointer", isCalendar && "hidden", className)}
        options={options}
        ref={mergedRef}
        {...props}
        render={renderComponent}
      />
    );
  },
);

DatePicker.displayName = "DatePicker";

export { DatePicker };
